import { LogOut, MapPin, Monitor, Moon, Sun } from 'lucide-react';
import { NavLink, Outlet } from 'react-router';
import { useLogout, useSession } from '../api/queries';
import { useTheme, type ThemePreference } from '../lib/theme';
import { useLiveFeed, type LiveStatus } from '../live/useLiveFeed';

const nextTheme: Record<ThemePreference, ThemePreference> = {
  system: 'light',
  light: 'dark',
  dark: 'system',
};
const themeIcon = { system: Monitor, light: Sun, dark: Moon };
const themeLabel = { system: 'Theme: follow system', light: 'Theme: light', dark: 'Theme: dark' };

function LiveIndicator({ status }: { status: LiveStatus }) {
  const live = status === 'live';
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400"
      title={live ? 'Receiving live updates' : 'Reconnecting to live updates'}
    >
      <span className={`h-2 w-2 rounded-full ${live ? 'bg-emerald-500' : 'animate-pulse bg-amber-500'}`} />
      <span className="hidden sm:inline">
        {live ? 'Live' : status === 'connecting' ? 'Connecting…' : 'Reconnecting…'}
      </span>
    </span>
  );
}

const navClass = ({ isActive }: { isActive: boolean }) =>
  `rounded-lg px-3 py-1.5 text-sm font-medium ${
    isActive
      ? 'bg-slate-200 text-slate-900 dark:bg-slate-800 dark:text-white'
      : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'
  }`;

export function AppShell() {
  const { data: session } = useSession();
  const logout = useLogout();
  const [theme, setTheme] = useTheme();
  const status = useLiveFeed(Boolean(session));
  const ThemeIcon = themeIcon[theme];

  return (
    <div className="flex h-full flex-col">
      <header className="z-[1000] flex h-14 shrink-0 items-center gap-2 border-b border-slate-200 bg-white px-3 sm:px-4 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center gap-1.5 font-semibold sm:mr-2">
          <MapPin className="h-5 w-5 text-blue-600" aria-hidden />
          <span className="sr-only sm:not-sr-only">Find My</span>
        </div>
        <nav className="flex gap-0.5 sm:gap-1">
          <NavLink to="/" end className={navClass}>
            Map
          </NavLink>
          <NavLink to="/devices" className={navClass}>
            Devices
          </NavLink>
          <NavLink to="/settings" className={navClass}>
            Settings
          </NavLink>
        </nav>
        <div className="ml-auto flex items-center gap-1 sm:gap-2">
          <LiveIndicator status={status} />
          <button
            type="button"
            onClick={() => setTheme(nextTheme[theme])}
            className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            aria-label={themeLabel[theme]}
            title={themeLabel[theme]}
          >
            <ThemeIcon className="h-5 w-5" />
          </button>
          <span className="hidden text-sm text-slate-500 md:inline">{session?.user.email}</span>
          <button
            type="button"
            onClick={() => logout.mutate()}
            className="inline-flex items-center gap-1 rounded-lg p-2 text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            <LogOut className="h-5 w-5" aria-hidden />
            <span className="hidden sm:inline">Log out</span>
          </button>
        </div>
      </header>
      <main className="relative min-h-0 flex-1">
        <Outlet />
      </main>
    </div>
  );
}
